import { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useApi, extractMembers } from '@/hooks/useApi';

const STORAGE_KEY = 'clubsome_selected_team';

const initialState = {
  selectedTeam: null,
  availableTeams: [],
  clubsWithTeams: [],
  isLoading: true,
  error: null,
};

export const TeamContext = createContext({
  ...initialState,
  selectTeam: () => {},
  refreshTeams: () => Promise.resolve(),
});

export function TeamProvider({ children }) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const api = useApi();

  const [selectedTeam, setSelectedTeam] = useState(null);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [clubsWithTeams, setClubsWithTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Build teams list from user roles
  const fetchTeams = useCallback(async () => {
    if (!isAuthenticated || authLoading || !user) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const roles = user.roles || [];
      const clubsMap = new Map();
      const teamsMap = new Map();

      // Process team_admin roles - these have all the info we need
      for (const role of roles) {
        if (role.roleType === 'team_admin' && role.metaType === 'team') {
          const teamId = role.metaId;
          const clubId = role.clubId;
          const clubName = role.clubName;
          const teamName = role.name;

          // Add team
          if (!teamsMap.has(teamId)) {
            teamsMap.set(teamId, {
              id: teamId,
              name: teamName,
              clubId,
              clubName,
              isClubAdmin: false,
            });
          }

          // Track club
          if (clubId && !clubsMap.has(clubId)) {
            clubsMap.set(clubId, {
              id: clubId,
              name: clubName,
              teams: [],
            });
          }
        }
      }

      // Process club_admin roles - need to fetch teams for these clubs
      const clubAdminClubIds = roles
        .filter((r) => r.roleType === 'club_admin' && r.metaType === 'club')
        .map((r) => r.metaId);

      // Fetch teams for clubs where user is club_admin
      for (const clubId of clubAdminClubIds) {
        try {
          // Get club details including teams
          const club = await api.get(`/clubs/${clubId}`);
          const clubName = club.name;

          // Ensure club is in map
          if (!clubsMap.has(clubId)) {
            clubsMap.set(clubId, {
              id: clubId,
              name: clubName,
              teams: [],
            });
          } else {
            // Update club name if we have it
            const existingClub = clubsMap.get(clubId);
            existingClub.name = clubName;
          }

          // Get teams for this club
          const teamsResponse = await api.get('/teams');
          const allTeams = extractMembers(teamsResponse);

          // Filter teams belonging to this club
          for (const team of allTeams) {
            const teamClubIri = team.club;
            const teamClubId = teamClubIri?.split('/').pop();

            if (teamClubId === clubId) {
              const teamId = team['@id']?.split('/').pop() || team.id;

              if (!teamsMap.has(teamId)) {
                teamsMap.set(teamId, {
                  id: teamId,
                  name: team.name,
                  clubId,
                  clubName,
                  isClubAdmin: true,
                });
              } else {
                // Mark existing team as club admin accessible
                teamsMap.get(teamId).isClubAdmin = true;
              }
            }
          }
        } catch (err) {
          console.error(`Failed to fetch teams for club ${clubId}:`, err);
        }
      }

      // Organize teams by club
      const teamsArray = Array.from(teamsMap.values());
      teamsArray.forEach((team) => {
        const club = clubsMap.get(team.clubId);
        if (club) {
          // Avoid duplicates
          if (!club.teams.some((t) => t.id === team.id)) {
            club.teams.push(team);
          }
        }
      });

      // Sort clubs and teams alphabetically
      const clubsArray = Array.from(clubsMap.values())
        .filter((club) => club.teams.length > 0)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      clubsArray.forEach((club) => {
        club.teams.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      });

      setAvailableTeams(teamsArray);
      setClubsWithTeams(clubsArray);

      // Restore selected team from storage or select first available
      const storedTeamId = localStorage.getItem(STORAGE_KEY);
      const storedTeam = teamsArray.find((t) => t.id === storedTeamId);

      if (storedTeam) {
        setSelectedTeam(storedTeam);
      } else if (teamsArray.length > 0) {
        setSelectedTeam(teamsArray[0]);
        localStorage.setItem(STORAGE_KEY, teamsArray[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch teams:', err);
      setError('Failed to load teams');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, authLoading, user, api]);

  // Fetch teams when auth state changes
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      fetchTeams();
    } else if (!authLoading && !isAuthenticated) {
      setIsLoading(false);
      setAvailableTeams([]);
      setClubsWithTeams([]);
      setSelectedTeam(null);
    }
  }, [authLoading, isAuthenticated, user, fetchTeams]);

  // Select a team
  const selectTeam = useCallback((team) => {
    setSelectedTeam(team);
    if (team) {
      localStorage.setItem(STORAGE_KEY, team.id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const value = useMemo(
    () => ({
      selectedTeam,
      availableTeams,
      clubsWithTeams,
      isLoading,
      error,
      selectTeam,
      refreshTeams: fetchTeams,
    }),
    [selectedTeam, availableTeams, clubsWithTeams, isLoading, error, selectTeam, fetchTeams]
  );

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}
